/**
 * Cloud VMs in the console (ADR 0106): state.ext.cloud from the control plane, kept live by SSE
 * 'ext', and its actions. In demo mode (?mock=1) the demo VMs are seeded once and every action
 * runs in memory, moving through the same transitional states.
 */
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { CLOUD_EXT, roleAtLeast } from '@orgus/protocol';
import type { CloudAdoptResult, CloudConnection, CloudDiscovery, CloudInstanceCandidate, CloudState, CloudVm, CloudVmCreate, Role } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { useMe } from '../auth/useMe.ts';
import { MOCK_AWS_CONNECTION, mockCloud, mockFleet, mockVm } from './mock.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const current = () => useOrgus.getState().ext[CLOUD_EXT] as CloudState | undefined;
const put = (s: CloudState) => useOrgus.getState().applyExt({ ext: CLOUD_EXT, type: 'state', data: s });

export interface CloudView {
  phase: 'loading' | 'error' | 'missing' | 'ready';
  state: CloudState | null;
  mock: boolean;
  /** The workspace's AWS connection, or null. */
  aws: CloudConnection | null;
  /** Owner or admin: may connect and disconnect. */
  canConnect: boolean;
  /** Operator and up: may create and control VMs. */
  canCreate: boolean;
}

function useMockSeed(): void {
  const { ready, mock, missing } = useOrgus(useShallow((s) => ({ ready: s.phase === 'ready', mock: s.mode === 'mock', missing: !s.ext[CLOUD_EXT] })));
  useEffect(() => {
    if (!mock || !ready || !missing) return;
    const st = useOrgus.getState();
    const now = Date.now();
    const { state, extraMachines } = mockCloud(Object.values(st.machines), now);
    // ?cloud=connect shows the demo before AWS is connected; ?cloud=adopt adds hand-made instances to adopt.
    const mode = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('cloud') : null;
    const connect = mode === 'connect';
    if (mode === 'adopt' || connect) {
      const fleet = mockFleet(Object.values(st.machines)[0], now);
      extraMachines.push(...fleet.machines);
      mockCandidates = mode === 'adopt' ? fleet.candidates : [];
    }
    if (extraMachines.length) st.applyPatch({ at: now, upserts: { machines: extraMachines }, removes: {} });
    put(connect ? { ...state, connections: [] } : state);
  }, [mock, ready, missing]);
}

export function useCloud(): CloudView {
  useMockSeed();
  const { phase, mode, state } = useOrgus(useShallow((s) => ({ phase: s.phase, mode: s.mode, state: (s.ext[CLOUD_EXT] as CloudState | undefined) ?? null })));
  const meRole = useMe((s) => s.me?.actor.role ?? null);
  return cloudView(phase, mode === 'mock', state, meRole);
}

/** The view from the store's phase, the module's state and the person's role (demo: an owner). */
export function cloudView(phase: string, mock: boolean, state: CloudState | null, meRole: Role | null): CloudView {
  const role: Role = mock ? 'owner' : (meRole ?? 'viewer');
  const aws = state?.connections.find((c) => c.provider === 'aws') ?? null;
  const base = { state, mock, aws, canConnect: roleAtLeast(role, 'admin'), canCreate: roleAtLeast(role, 'operator') };
  if (phase === 'loading' || phase === 'error') return { phase, ...base };
  if (!state) return { phase: mock ? 'loading' : 'missing', ...base };
  return { phase: 'ready', ...base };
}

// ---------------------------------------------------------------------------
// Demo: the same transitions in memory
// ---------------------------------------------------------------------------

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** The demo's instances to adopt (?cloud=adopt). */
let mockCandidates: CloudInstanceCandidate[] = [];

function mockUpdate(f: (s: CloudState) => CloudState): void {
  const s = current();
  if (s) put(f(s));
}

function mockVmUpdate(id: string, patch: Partial<CloudVm>): void {
  mockUpdate((s) => ({ ...s, vms: s.vms.map((v) => (v.id === id ? { ...v, ...patch, updatedAt: Date.now() } : v)) }));
}

/** In the demo a VM settles after a short pause, as DescribeInstances would confirm it. */
function mockSettle(id: string, patch: Partial<CloudVm>, ms = 2500): void {
  setTimeout(() => mockVmUpdate(id, patch), ms);
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export async function setupAws(region?: string): Promise<CloudConnection> {
  if (isMock()) {
    await wait(300);
    const pending: CloudConnection = { ...MOCK_AWS_CONNECTION(Date.now()), state: 'pending', roleArn: null, accountId: null, connectedBy: null, connectedAt: null, test: null };
    mockUpdate((s) => ({ ...s, connections: [pending] }));
    return pending;
  }
  return api<CloudConnection>('/api/cloud/aws/setup', { method: 'POST', body: region ? { region } : {} });
}

export async function connectAws(roleArn: string, region: string): Promise<CloudConnection> {
  if (isMock()) {
    await wait(600);
    const c = current()?.connections[0];
    if (!c) throw new Error('demo has no connection');
    const accountId = /::(\d{12}):/.exec(roleArn)?.[1] ?? null;
    const next = { ...c, state: 'connected' as const, roleArn, region, accountId, connectedBy: 'you', connectedAt: Date.now(), test: { at: Date.now(), ok: true, message: `Orgus can act in AWS account ${accountId ?? ''}.` } };
    mockUpdate((s) => ({ ...s, connections: [next] }));
    return next;
  }
  return api<CloudConnection>('/api/cloud/aws/connect', { method: 'POST', body: { roleArn, region } });
}

export async function testAws(): Promise<CloudConnection> {
  if (isMock()) {
    await wait(500);
    return current()!.connections[0]!;
  }
  return api<CloudConnection>('/api/cloud/aws/test', { method: 'POST', body: {} });
}

export async function disconnectAws(): Promise<void> {
  if (isMock()) {
    await wait(300);
    mockUpdate((s) => ({ ...s, connections: [] }));
    return;
  }
  await api('/api/cloud/aws', { method: 'DELETE' });
}

export async function createVm(input: CloudVmCreate): Promise<CloudVm> {
  if (isMock()) {
    await wait(700);
    const id = `vm_demo_${Math.random().toString(36).slice(2, 8)}`;
    const vm = mockVm({ id, name: input.name ?? id, size: input.size ?? 't3.medium', region: input.region ?? 'us-east-1', lifetime: input.lifetime ?? 'ephemeral', state: 'pending', createdAt: Date.now(), repoUrl: input.repoUrl ?? null }, Date.now());
    mockUpdate((s) => ({ ...s, vms: [...s.vms, vm] }));
    mockSettle(id, { state: 'running' });
    return vm;
  }
  return api<CloudVm>('/api/cloud/vms', { method: 'POST', body: input });
}

export type VmAction = 'stop' | 'start' | 'restart' | 'keep';

export async function vmAction(vm: CloudVm, action: VmAction, o: { waitMinutes?: number } = {}): Promise<CloudVm> {
  if (isMock()) {
    await wait(300);
    const now = Date.now();
    if (action === 'keep') mockVmUpdate(vm.id, { lifetime: 'permanent' });
    else if (action === 'stop') {
      mockVmUpdate(vm.id, { state: 'stopping', op: { kind: 'stop', by: 'you', at: now, waitUntil: null }, idleSince: null });
      mockSettle(vm.id, { state: 'stopped', op: null });
    } else if (action === 'start') {
      mockVmUpdate(vm.id, { state: 'pending', op: { kind: 'start', by: 'you', at: now, waitUntil: null } });
      mockSettle(vm.id, { state: 'running', op: null });
    } else {
      mockVmUpdate(vm.id, { op: { kind: 'restart', by: 'you', at: now, waitUntil: null } });
      mockSettle(vm.id, { op: null });
    }
    return current()!.vms.find((v) => v.id === vm.id)!;
  }
  return api<CloudVm>(`/api/cloud/vms/${encodeURIComponent(vm.id)}/${action}`, { method: 'POST', body: action === 'stop' && o.waitMinutes !== undefined ? { waitMinutes: o.waitMinutes } : {} });
}

export async function deleteVm(vm: CloudVm): Promise<void> {
  if (isMock()) {
    await wait(300);
    mockVmUpdate(vm.id, { state: 'shutting-down', op: { kind: 'delete', by: 'you', at: Date.now(), waitUntil: null } });
    setTimeout(() => mockUpdate((s) => ({ ...s, vms: s.vms.filter((v) => v.id !== vm.id) })), 2500);
    return;
  }
  await api(`/api/cloud/vms/${encodeURIComponent(vm.id)}`, { method: 'DELETE' });
}

/** The instances in the connected region this workspace may adopt (owner, admin). */
export async function discoverAws(): Promise<CloudDiscovery> {
  if (isMock()) {
    await wait(400);
    return { region: current()?.connections[0]?.region ?? 'us-east-1', at: Date.now(), candidates: mockCandidates };
  }
  return api<CloudDiscovery>('/api/cloud/aws/instances');
}

/** Adopt instances: Orgus tags them for the workspace and controls them from Machines. */
export async function adoptVms(instanceIds: string[]): Promise<CloudAdoptResult> {
  if (isMock()) {
    await wait(700);
    const now = Date.now();
    const take = mockCandidates.filter((c) => instanceIds.includes(c.instanceId) && c.adoptable);
    const adopted = take.map((c) => mockVm({ id: `vm_adopt_${c.instanceId.slice(-4)}`, name: c.name ?? c.instanceId, instanceId: c.instanceId, machineId: c.machineId, state: c.state, size: c.size, lifetime: 'permanent', createdAt: now, adoptedAt: now, ...(c.privateDns ? { privateDns: c.privateDns } : {}) }, now));
    mockCandidates = mockCandidates.filter((c) => !take.includes(c));
    mockUpdate((s) => ({ ...s, vms: [...s.vms, ...adopted] }));
    return { adopted, failed: instanceIds.filter((id) => !take.some((c) => c.instanceId === id)).map((instanceId) => ({ instanceId, error: 'Add the tag orgus=fleet to it in the AWS console, then adopt it.' })) };
  }
  // 200 with the result, also when none was adopted: each failure says why.
  return api<CloudAdoptResult>('/api/cloud/aws/adopt', { method: 'POST', body: { instanceIds } });
}

/** A permanent VM's Stop when idle rule: minutes, or null for never. */
export async function setIdleStop(vm: CloudVm, minutes: number | null): Promise<CloudVm> {
  if (isMock()) {
    await wait(200);
    mockVmUpdate(vm.id, { idleStopMs: minutes === null ? null : minutes * 60_000 });
    return current()!.vms.find((v) => v.id === vm.id)!;
  }
  return api<CloudVm>(`/api/cloud/vms/${encodeURIComponent(vm.id)}/rules`, { method: 'PUT', body: { idleStopMinutes: minutes } });
}
